import React, { useState, useCallback, createContext, useContext } from "react";
import Sidebar from "./Sidebar";
import { ChevronLeft } from "lucide-react";
import useIsMobile from "@/hooks/useIsMobile";

/**
 * Context for mobile drill-down navigation.
 * Pages call `drillIn()` when user selects an item in the column to show main content.
 * The back button calls `drillOut()` to return to the column view.
 */
const MobileDrillCtx = createContext(null);
export function useMobileDrill() {
  return useContext(MobileDrillCtx);
}

/**
 * AppLayout wraps every authenticated page.
 *
 * Desktop: Horizontal flex — Sidebar | column | Main content
 * Mobile:  Bottom tab bar + drill-down — shows either column or main content.
 */
export default function AppLayout({ children, column, mobileTitle, mobileManaged, hideSidebar }) {
  const isMobile = useIsMobile();
  const [mobileShowMain, setMobileShowMain] = useState(!column);

  const drillIn = useCallback(() => setMobileShowMain(true), []);
  const drillOut = useCallback(() => setMobileShowMain(false), []);

  const drillCtx = { drillIn, drillOut, isMobile, isShowingMain: mobileShowMain };

  // ── Desktop layout ──────────────────────────────────────────────────────────
  if (!isMobile) {
    return (
      <MobileDrillCtx.Provider value={drillCtx}>
        <div className="flex h-screen overflow-hidden bg-gray-50" data-testid="app-layout">
          {!hideSidebar && <Sidebar />}
          {column}
          <main className="flex-1 overflow-y-auto">
            {children}
          </main>
        </div>
      </MobileDrillCtx.Provider>
    );
  }

  // ── Mobile layout — page manages its own panels ─────────────────────────────
  // Reached only when `isMobile` is true (see early return above), so no
  // inner `isMobile ?` ternary is needed here.
  if (mobileManaged) {
    return (
      <MobileDrillCtx.Provider value={drillCtx}>
        <div className="flex flex-col h-screen bg-gray-50" data-testid="app-layout">
          {/* Stack column + main as absolutely positioned overlays so
              each occupies the full viewport. Each consumer page
              toggles visibility on its SecondaryColumns + content via
              Tailwind `hidden` classes (e.g. ClientsPage's
              `mobilePanel` state). Without absolute overlay,
              `<main className="flex-1">` would still grab ~half the
              viewport even when its child is `hidden`, squashing the
              visible column. The overlay model lets each page's own
              state machine drive which surface is on top without
              flex-shrink fights.

              Layer order matters: <main> is rendered FIRST so the
              column overlay sits on top. Otherwise <main> (even when
              its child has `display:none`) absorbs every tap on the
              column rows below it.

              Sidebar clearance: the bottom tab bar is `position:fixed`,
              56px tall + iOS safe-area inset. We CANNOT use
              `paddingBottom` on this wrapper because absolutely
              positioned children resolve `inset:0` against the parent's
              PADDING BOX (which includes the padding) — i.e. padding
              is a no-op for absolute children. Instead, we set
              `bottom` directly on each absolute child to
              `56px + safe-area-inset` so the column + main both end
              ABOVE the fixed sidebar. */}
          <div
            className="relative flex-1 overflow-hidden"
            data-testid="mobile-managed-stack"
          >
            <main
              className="absolute left-0 right-0 top-0 overflow-y-auto"
              style={{ bottom: hideSidebar
                ? 0
                : "calc(56px + env(safe-area-inset-bottom, 0px))" }}
            >
              {children}
            </main>
            {/* Column wrapper is pointer-events-none so when ALL its
                SecondaryColumn children are display:none (i.e. the
                page is showing main content via mobilePanel="detail"),
                clicks pass through to <main> below. The
                SecondaryColumn children re-enable pointer events on
                themselves so they remain interactive when visible. */}
            <div
              className="absolute left-0 right-0 top-0 flex overflow-hidden pointer-events-none"
              style={{ bottom: hideSidebar
                ? 0
                : "calc(56px + env(safe-area-inset-bottom, 0px))" }}
            >
              {column}
            </div>
          </div>
          {!hideSidebar && <Sidebar />}
        </div>
      </MobileDrillCtx.Provider>
    );
  }

  // ── Mobile layout — AppLayout drill-down ────────────────────────────────────
  return (
    <MobileDrillCtx.Provider value={drillCtx}>
      <div className="flex flex-col h-screen bg-gray-50" data-testid="app-layout">
        {column ? (
          <>
            {mobileShowMain && (
              <div
                className="flex items-center h-11 px-2 flex-shrink-0 bg-white border-b border-gray-200 z-10"
                data-testid="mobile-back-bar"
              >
                <button
                  onClick={drillOut}
                  data-testid="mobile-back-btn"
                  className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-sm font-medium
                             text-[#001C60] hover:bg-gray-100 transition-colors"
                >
                  <ChevronLeft size={18} strokeWidth={2.5} />
                  <span>{mobileTitle || "Back"}</span>
                </button>
              </div>
            )}
            <div
              className="flex-1 overflow-hidden"
              style={{ paddingBottom: hideSidebar
                ? 0
                : "calc(56px + env(safe-area-inset-bottom, 0px))" }}
            >
              {mobileShowMain ? (
                <main className="h-full overflow-y-auto">{children}</main>
              ) : (
                <div className="h-full overflow-y-auto">{column}</div>
              )}
            </div>
          </>
        ) : (
          <div
            className="flex-1 overflow-hidden"
            style={{ paddingBottom: hideSidebar
              ? 0
              : "calc(56px + env(safe-area-inset-bottom, 0px))" }}
          >
            <main className="h-full overflow-y-auto">{children}</main>
          </div>
        )}
        {!hideSidebar && <Sidebar />}
      </div>
    </MobileDrillCtx.Provider>
  );
}
